import React, { useEffect, useState } from "react";
import api, { fmtErr } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import LetterViewer from "../components/LetterViewer";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Stamp, XCircle, Eye, FileText } from "lucide-react";
import { toast } from "sonner";

export default function PendingApprovals() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [letterTxn, setLetterTxn] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [reprints, setReprints] = useState([]);
  const [reprintRemarks, setReprintRemarks] = useState({});

  const load = () => {
    api.get("/protocol/pending").then((r) => setRows(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/tickets/reprint-requests").then((r) => setReprints(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const act = async (action) => {
    setLoading(true);
    try {
      await api.post(`/protocol/${selected.transaction_id}/${action}`, { remarks });
      toast.success(action === "approve" ? "Request approved" : "Request rejected");
      setSelected(null);
      setRemarks("");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="pending-approvals-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Pending Approvals</h1>
      <p className="text-sm text-slate-500 mb-5">Review and approve protocol darshan requests.</p>

      <Card className="shadow-sm">
        <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Awaiting Approval ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
                <th className="py-2 pr-4">Transaction ID</th><th className="py-2 pr-4">Devotee</th><th className="py-2 pr-4">Reference</th>
                <th className="py-2 pr-4">Mobile</th><th className="py-2 pr-4">Devotees</th><th className="py-2 pr-4">Darshan</th>
                <th className="py-2 pr-4">Paid/Free</th><th className="py-2 pr-4">Submitted</th><th className="py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={9} className="py-8 text-center text-slate-400" data-testid="pending-empty">No pending requests</td></tr>}
              {rows.map((t) => (
                <tr key={t.transaction_id} className="border-b border-[#F1EADB] hover:bg-gold-50/50" data-testid={`pending-row-${t.transaction_id}`}>
                  <td className="py-2 pr-4 font-mono text-xs">{t.transaction_id}</td>
                  <td className="py-2 pr-4">{t.devotee_name}</td>
                  <td className="py-2 pr-4">{t.reference_name || "-"}</td>
                  <td className="py-2 pr-4">{t.mobile}</td>
                  <td className="py-2 pr-4">{t.num_devotees}</td>
                  <td className="py-2 pr-4">{t.darshan_type_name}</td>
                  <td className="py-2 pr-4">{t.paid_free}</td>
                  <td className="py-2 pr-4 text-xs text-slate-500">{new Date(t.created_at).toLocaleString("en-IN")}</td>
                  <td className="py-2 whitespace-nowrap">
                    {t.letter_file?.name && (
                      <Button size="sm" variant="outline" className="mr-2 border-maroon-300 text-maroon-800" data-testid={`pending-letter-${t.transaction_id}`}
                        onClick={() => setLetterTxn(t)}>
                        <FileText className="w-4 h-4 mr-1" /> Letter
                      </Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`pending-view-${t.transaction_id}`}
                      onClick={() => { setSelected(t); setRemarks(""); }}>
                      <Eye className="w-4 h-4 mr-1" /> Review
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {reprints.length > 0 && (
        <Card className="shadow-sm mt-5" data-testid="reprint-requests-card">
          <CardHeader><CardTitle className="font-heading text-lg text-maroon-900">Ticket Reprint Requests ({reprints.length})</CardTitle></CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-gold-700 border-b border-[#E5D9C5]">
                  <th className="py-2 pr-4">Transaction ID</th><th className="py-2 pr-4">Ticket No</th><th className="py-2 pr-4">Devotee</th>
                  <th className="py-2 pr-4">Requested By</th><th className="py-2 pr-4">Reason</th><th className="py-2 pr-4">Remarks</th><th className="py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {reprints.map((t) => (
                  <tr key={t.transaction_id} className="border-b border-[#F1EADB]" data-testid={`reprint-row-${t.transaction_id}`}>
                    <td className="py-2 pr-4 font-mono text-xs">{t.transaction_id}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{t.ticket.ticket_no}</td>
                    <td className="py-2 pr-4">{t.devotee_name}</td>
                    <td className="py-2 pr-4">{t.ticket.reprint_request.requested_by}</td>
                    <td className="py-2 pr-4 text-xs">{t.ticket.reprint_request.reason}</td>
                    <td className="py-2 pr-4">
                      <Textarea rows={1} className="min-w-36" value={reprintRemarks[t.transaction_id] || ""}
                        onChange={(e) => setReprintRemarks({ ...reprintRemarks, [t.transaction_id]: e.target.value })}
                        data-testid={`reprint-remarks-${t.transaction_id}`} />
                    </td>
                    <td className="py-2 flex gap-1">
                      <Button size="sm" className="bg-green-700 hover:bg-green-800 text-white" data-testid={`reprint-approve-${t.transaction_id}`}
                        onClick={async () => {
                          try {
                            await api.post(`/tickets/${t.transaction_id}/reprint/approve`, { remarks: reprintRemarks[t.transaction_id] || "" });
                            toast.success("Reprint approved"); load();
                          } catch (e) { toast.error(fmtErr(e)); }
                        }}>Approve</Button>
                      <Button size="sm" variant="outline" className="border-red-300 text-red-700" data-testid={`reprint-reject-${t.transaction_id}`}
                        onClick={async () => {
                          try {
                            await api.post(`/tickets/${t.transaction_id}/reprint/reject`, { remarks: reprintRemarks[t.transaction_id] || "" });
                            toast.success("Reprint rejected"); load();
                          } catch (e) { toast.error(fmtErr(e)); }
                        }}>Reject</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={!!letterTxn} onOpenChange={(o) => !o && setLetterTxn(null)}>
        <DialogContent className="max-w-4xl max-h-[94vh] overflow-y-auto" data-testid="pending-letter-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Recommendation Letter · {letterTxn?.transaction_id}</DialogTitle></DialogHeader>
          {letterTxn && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{letterTxn.devotee_name} · Ref: {letterTxn.reference_name || "-"} · Letterhead: {letterTxn.letterhead_name || "-"}</p>
              <LetterViewer txnId={letterTxn.transaction_id} autoLoad />
              <div className="flex justify-end">
                <Button onClick={() => { setSelected(letterTxn); setRemarks(""); setLetterTxn(null); }} className="bg-maroon-700 hover:bg-maroon-800 text-white">
                  <Eye className="w-4 h-4 mr-1" /> Review &amp; Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="approval-dialog">
          <DialogHeader><DialogTitle className="font-heading text-maroon-900">Protocol Request Review</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                <div><p className="text-xs text-gold-700 font-bold uppercase">Transaction ID</p><p className="font-mono">{selected.transaction_id}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Status</p><StatusBadge status={selected.status} /></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Devotee Name</p><p>{selected.devotee_name}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Reference</p><p>{selected.reference_name || "-"}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Mobile</p><p>{selected.mobile}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Devotees</p><p>{selected.num_devotees}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Darshan Type</p><p>{selected.darshan_type_name}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Paid / Free</p><p>{selected.paid_free} {selected.amount > 0 ? `· ₹${selected.amount}` : ""}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Letter Date</p><p>{selected.letter_date || "-"}</p></div>
                <div><p className="text-xs text-gold-700 font-bold uppercase">Letterhead</p><p>{selected.letterhead_name || "-"}</p></div>
              </div>
              {selected.devotees?.length > 0 && (
                <div className="text-sm">
                  <p className="text-xs text-gold-700 font-bold uppercase mb-1">Devotee Records</p>
                  {selected.devotees.map((d, i) => (
                    <p key={i} className="text-xs text-slate-600">{i + 1}. {d.name} {d.aadhaar ? `· Aadhaar ${d.aadhaar}` : ""} {d.age ? `· ${d.age} yrs` : ""}</p>
                  ))}
                </div>
              )}
              <LetterViewer txnId={selected.transaction_id} />
              {selected.remarks && <div className="text-sm"><p className="text-xs text-gold-700 font-bold uppercase">Remarks</p><p>{selected.remarks}</p></div>}
              <div>
                <p className="text-xs text-gold-700 font-bold uppercase mb-1">Approval Remarks</p>
                <Textarea data-testid="approval-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} placeholder="Remarks (required for rejection)" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button data-testid="approval-reject-btn" variant="outline" disabled={loading}
                  className="border-red-300 text-red-700 hover:bg-red-50" onClick={() => act("reject")}>
                  <XCircle className="w-4 h-4 mr-1" /> Reject
                </Button>
                <Button data-testid="approval-approve-btn" disabled={loading}
                  className="bg-green-700 hover:bg-green-800 text-white" onClick={() => act("approve")}>
                  <Stamp className="w-4 h-4 mr-1" /> Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
